import { useEffect, useState } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";

import { AudioPlayerProvider } from "@modules/content/AudioPlayerContext";
import { setIsCurrentChapterEnded } from "@lib/stores/content";
import { ErrorFallback } from "@components/items/display/ErrorFallback";

import { routes } from "./utils/constants/routes";
import { requireAuth } from "./utils/hocs/requireAuth";
import { requirePublic } from "./utils/hocs/requirePublic";
import { useAppSelector } from "./lib/stores";
import { loadAuthorizedData } from "./utils/hooks/useCachedResources";
import { PlayerLayout } from "./layouts/PlayerLayout";
import { useAppNavigate } from "@utils/hooks/useAppNavigate";
import { ShadcnButton } from "@components/items/display/ShadcnButton";
import TranslatedText from "@components/items/display/TranslatedText";
import { getStorageSync } from "@modules/storage/storageHelper";
import { requireAutoAuth } from "@utils/hocs/requireAutoAuth";
import { getQueryFromUrl } from "@utils/helpers/getQueryFromUrl";

export function App() {
  const { pathname } = useLocation();

  const forceAuthenticate = getQueryFromUrl("forceAuthenticate");

  const autoAuthenticated = getStorageSync("AUTO_AUTHENTICATED");

  const [isNavigated, setIsNavigated] = useState(false);

  const isAuthenticated = useAppSelector(({ auth }) =>
    Boolean(auth.tokens?.token)
  );

  const userAgent = navigator.userAgent.toLowerCase();

  useEffect(() => {
    if (
      isAuthenticated &&
      (!pathname.includes("/login/subscribed") ||
        (pathname.includes("/login/subscribed") &&
          autoAuthenticated &&
          forceAuthenticate === "false"))
    ) {
      loadAuthorizedData();
    }
  }, [isAuthenticated, pathname, autoAuthenticated, forceAuthenticate]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (isNavigated) {
      timeout = setTimeout(() => {
        setIsNavigated(false);
      }, 1350);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [isNavigated]);

  const renderRoutes = () => {
    return routes.map((route) => {
      const { pathname, authenticated, Component, Layout } = route;

      const ComponentWithLayout = () => {
        if (Layout) {
          return (
            <Layout>
              <Component />
            </Layout>
          );
        }

        return <Component />;
      };

      return (
        <Route
          key={pathname}
          path={pathname}
          element={
            authenticated
              ? requireAuth(
                  <ErrorWrapper>
                    <ComponentWithLayout />
                  </ErrorWrapper>
                )
              : authenticated === false
                ? requirePublic(
                    <ErrorWrapper>
                      <ComponentWithLayout />
                    </ErrorWrapper>
                  )
                : requireAutoAuth(
                    <ErrorWrapper>
                      <ComponentWithLayout />
                    </ErrorWrapper>
                  )
          }
        />
      );
    });
  };

  const isMobileScreen =
    !userAgent.includes("faylasof") &&
    !userAgent.includes("wajeez") &&
    !window.location.origin.includes("auth.wajeez.com")
      ? true
      : isAuthenticated
        ? true
        : false;

  return (
    <div className="h-screen w-full dark:bg-black">
      <AudioPlayerProvider
        onStart={() => {
          setIsCurrentChapterEnded(false);
        }}
        onEnd={() => {
          setIsCurrentChapterEnded(true);
        }}
      >
        <PlayerLayout>
          <Routes>{renderRoutes()}</Routes>
        </PlayerLayout>
      </AudioPlayerProvider>
      {isMobileScreen ? (
        <div className="fixed left-0 top-0 z-[9999] flex h-screen w-screen flex-col items-center justify-center bg-white px-4 sm:hidden dark:bg-black">
          {!isNavigated ? (
            <>
              <div className="text-center text-[18px] text-black dark:text-white">
                <TranslatedText text={`layout.mobile screens message`} />
              </div>
              <div
                onClick={() => {
                  setIsNavigated(true);

                  const initUrl = getStorageSync("INIT_URL");

                  setTimeout(() => {
                    window.location.href = `wajeez://${initUrl}`;
                  }, 300);

                  setTimeout(() => {
                    window.location.href = "https://wajeez.page.link/invite";
                  }, 550);
                }}
              >
                <ShadcnButton className="mt-10 px-6 text-white">
                  <TranslatedText
                    text={`layout.Go to the app`}
                    className="text-[16px] font-normal leading-[22px]"
                  />
                </ShadcnButton>
              </div>
            </>
          ) : (
            <div className="h-14 w-14">
              <div className="h-full w-full">
                <div className="h-full w-full animate-spin rounded-full border-[3px] border-black border-b-transparent dark:border-white dark:border-b-transparent"></div>
              </div>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

const ErrorWrapper = ({ children }: { children: JSX.Element }) => {
  const navigate = useAppNavigate();

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={(details) => {
        navigate("/");
      }}
    >
      {children}
    </ErrorBoundary>
  );
};

export default App;
